/**
 * AI Utilities Mixin
 * Provides common utility methods for components using AI services
 */
export default {
  methods: {
    /**
     * Asynchronous delay utility
     * @param {number} ms - Milliseconds to wait
     * @param {boolean} random - If true, applies random delay between 0 and ms
     * @returns {Promise} Promise that resolves after the delay
     *
     * @example
     * await this.delay(1000) // Wait exactly 1 second
     * await this.delay(1000, true) // Wait random time between 0-1 second
     */
    delay(ms, random = false) {
      const delayTime = random
        ? Math.floor(Math.random() * ms)
        : ms
      return new Promise(resolve => setTimeout(resolve, delayTime))
    },

    /**
     * Formats object as indented JSON string
     * @param {*} data - Data to format as JSON
     * @returns {string} Formatted JSON string with 2-space indentation
     *
     * @example
     * this.formatJson({name: 'John', age: 30})
     * // Returns: "{\n  \"name\": \"John\",\n  \"age\": 30\n}"
     */
    formatJson(data) {
      return JSON.stringify(data, null, 2)
    },

    /**
     * Splits an array into batches of specified size
     * Each item in the batch gets an additional 'index' property with its original position
     * @param {Array} array - Array to split into batches
     * @param {number} size - Size of each batch
     * @returns {Array<Array>} Array of batches, each containing up to 'size' items
     *
     * @example
     * this.createBatches([1, 2, 3, 4, 5], 2)
     * // Returns: [[{...1, index: 0}, {...2, index: 1}], [{...3, index: 2}, {...4, index: 3}], [{...5, index: 4}]]
     */
    createBatches(array, size) {
      const batches = []
      for (let i = 0; i < array.length; i += size) {
        const batch = array.slice(i, i + size).map((item, index) => ({
          ...item,
          index: i + index
        }))
        batches.push(batch)
      }
      return batches
    },

    /**
     * Determines Bootstrap badge variant based on confidence score
     * @param {number} confidence - Confidence score (0-100)
     * @returns {string} Bootstrap badge variant ('success', 'warning', 'info', or 'danger')
     *
     * @example
     * this.getConfidenceBadgeVariant(95) // Returns: 'success'
     * this.getConfidenceBadgeVariant(80) // Returns: 'warning'
     * this.getConfidenceBadgeVariant(60) // Returns: 'info'
     * this.getConfidenceBadgeVariant(40) // Returns: 'danger'
     */
    getConfidenceBadgeVariant(confidence) {
      if (confidence >= 90) return 'success'
      if (confidence >= 75) return 'warning'
      if (confidence >= 50) return 'info'
      return 'danger'
    },

    /**
     * Parses AI JSON response with markdown cleanup
     * Removes markdown code blocks (```json and ```) before parsing
     * @param {string} responseText - Raw response text that may contain markdown-wrapped JSON
     * @returns {Object|null} Parsed JSON object or null if parsing fails
     *
     * @example
     * this.parseJsonResponse('```json\n{"status": "ok"}\n```')
     * // Returns: {status: 'ok'}
     */
    parseJsonResponse(responseText) {
      try {
        const cleaned = responseText
          .replaceAll('```json', '')
          .replaceAll('```', '')
          .trim()
        return JSON.parse(cleaned)
      } catch (error) {
        console.error('Error parsing JSON response:', error, responseText)
        return null
      }
    },

    /**
     * Parses AI response in TOON format (Token-Oriented Object Notation)
     *
     * TOON is a structured format designed for AI responses that supports both
     * tabular array data and simple key-value pairs. This parser automatically
     * detects the format type and applies appropriate parsing logic with type
     * conversion for common data types (numbers, booleans, quoted strings).
     *
     * Handles multiple TOON response formats:
     * 1. Simple key:value format (e.g., "confidence: 95")
     * 2. Multi-line key:value format (e.g., negotiation responses with multiple fields)
     * 3. Tabular format with arrays (e.g., "data[N]{fields}:", "autocomplete[N]{fields}:")
     * 4. Error responses (e.g., "error: message" or "data[0]:\nerror: message")
     *
     * @param {string} text - Raw AI response text in TOON format (may include ```tool markdown wrappers)
     * @param {Object} options - Optional configuration
     * @param {Function} options.validator - Custom validation function (receives parsed object, should throw if invalid)
     * @param {string} options.expectedArrayKey - Expected array key name ('data', 'autocomplete', etc.)
     * @returns {Object|null} Parsed object with automatic type conversion or null if parsing fails
     *
     * @example
     * // Simple format
     * this.parseToonResponse('confidence: 95') // Returns: {confidence: 95}
     *
     * @example
     * // Multi-line format with type conversion
     * this.parseToonResponse('message: Hello\nended: false\ncurrentOffer: 100')
     * // Returns: {message: 'Hello', ended: false, currentOffer: 100}
     *
     * @example
     * // Tabular format with quoted strings containing commas
     * this.parseToonResponse('data[2]{guess,confidence}:\n"Address 1, City",95\n"Address 2, Town",85')
     * // Returns: {data: [{guess: 'Address 1, City', confidence: 95}, {guess: 'Address 2, Town', confidence: 85}]}
     *
     * @example
     * // Error format with empty array
     * this.parseToonResponse('data[0]:\nerror: "Invalid address"')
     * // Returns: {data: [], error: 'Invalid address'}
     *
     * @example
     * // With custom validator
     * const validator = (result) => {
     *   if (!result.message) throw new Error('Missing message field')
     * }
     * this.parseToonResponse('message: Hello\nended: true', { validator })
     * // Returns: {message: 'Hello', ended: true} (or throws if validation fails)
     */
    parseToonResponse(text, options = {}) {
      try {
        // Clean markdown code blocks if present
        const cleaned = text
          .replaceAll('```tool', '')
          .replaceAll('```', '')
          .trim()

        const lines = cleaned.split('\n').filter(line => line.trim())

        if (!lines.length) {
          console.error('Empty TOON response')
          return null
        }

        // Check for error response format: "error: message" or "data[0]:\nerror: message"
        const errorLine = lines.find(line => line.trim().startsWith('error:'))
        if (errorLine) {
          const errorValue = errorLine.substring(errorLine.indexOf(':') + 1).trim()
          const error = errorValue.startsWith('"') && errorValue.endsWith('"')
            ? errorValue.slice(1, -1)
            : errorValue

          // If there's an expectedArrayKey, return it as empty array with error
          if (options.expectedArrayKey) {
            return { [options.expectedArrayKey]: [], error }
          }
          return { data: [], error }
        }

        // Check for tabular format: "arrayName[N]{fields}:" or "arrayName[N]:"
        const tabularHeaderLine = lines.find(line =>
          line.match(/^[a-zA-Z_]+\[\d+\](\{[^}]+\})?:/)
        )

        if (tabularHeaderLine) {
          return this.parseToonTabularFormat(lines, tabularHeaderLine, options)
        }

        // Check for simple key:value format (single line or multi-line)
        return this.parseToonKeyValueFormat(lines, options)
      } catch (error) {
        console.error('Error parsing TOON format:', error, text)
        return null
      }
    },

    /**
     * Parses TOON tabular format (arrays with structured data)
     *
     * Handles tabular TOON responses where data is presented in array format with
     * column headers. Automatically extracts column definitions and parses CSV-style
     * rows with proper quote handling for strings containing commas.
     *
     * @private
     * @param {Array<string>} lines - All cleaned lines from the TOON response
     * @param {string} headerLine - The header line containing array definition (e.g., "data[2]{field1,field2}:")
     * @param {Object} options - Parsing options from parent call
     * @param {Function} options.validator - Optional validator function to validate final result
     * @returns {Object|null} Parsed object with array data under the specified key, or null if parsing fails
     *
     * @example
     * // Input:
     * // data[2]{guess,confidence}:
     * // "Main St, City",95
     * // "Second Ave",80
     * this.parseToonTabularFormat(lines, 'data[2]{guess,confidence}:', {})
     * // Returns: {data: [{guess: 'Main St, City', confidence: 95}, {guess: 'Second Ave', confidence: 80}]}
     */
    parseToonTabularFormat(lines, headerLine, options) {
      try {
        // Extract array key and columns from header
        // Format: "arrayName[count]{field1,field2,...}:"
        const headerMatch = headerLine.match(/^([a-zA-Z_]+)\[(\d+)\](\{([^}]+)\})?:/)
        if (!headerMatch) {
          console.error('Invalid TOON tabular header format:', headerLine)
          return null
        }

        const arrayKey = headerMatch[1] // e.g., "data", "autocomplete"
        // const expectedCount = parseInt(headerMatch[2]) // e.g., 2 (not used currently)
        const columnsStr = headerMatch[4] // e.g., "guess,confidence"

        // If columns are specified in header, parse them
        const columns = columnsStr ? columnsStr.split(',').map(c => c.trim()) : []

        // Parse data rows (everything after the header line)
        const dataStartIndex = lines.indexOf(headerLine) + 1
        const dataLines = lines.slice(dataStartIndex)

        const data = []
        for (const line of dataLines) {
          // Skip empty lines or lines that look like headers
          if (!line.trim() || line.includes('[')) continue

          const parsedRow = this.parseToonTabularRow(line, columns)
          if (parsedRow && Object.keys(parsedRow).length > 0) {
            data.push(parsedRow)
          }
        }

        console.log('Parsed AI TOON (tabular):', { [arrayKey]: data })

        const result = { [arrayKey]: data }

        // Apply custom validator if provided
        if (options.validator && !options.validator(result)) {
          console.error('TOON validation failed:', result)
          return null
        }

        return result
      } catch (error) {
        console.error('Error parsing TOON tabular format:', error)
        return null
      }
    },

    /**
     * Parses a single row from tabular TOON format
     *
     * Handles CSV-style row parsing with proper quote handling for values containing
     * commas. Automatically converts values to appropriate types (numbers, strings)
     * based on column names and value patterns.
     *
     * @private
     * @param {string} line - CSV-style line to parse (e.g., '"Value 1, with comma",123,true')
     * @param {Array<string>} columns - Column names from header definition
     * @returns {Object} Parsed row as object with column names as keys and converted values
     *
     * @example
     * this.parseToonTabularRow('"Main St, City",95', ['guess', 'confidence'])
     * // Returns: {guess: 'Main St, City', confidence: 95}
     *
     * @example
     * this.parseToonTabularRow('Simple Value,80', ['text', 'score'])
     * // Returns: {text: 'Simple Value', score: 80}
     */
    parseToonTabularRow(line, columns) {
      // Split by comma, but respect quoted values
      const values = []
      let current = ''
      let inQuotes = false

      for (let i = 0; i < line.length; i++) {
        const char = line[i]
        if (char === '"') {
          inQuotes = !inQuotes
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim())
          current = ''
        } else {
          current += char
        }
      }
      values.push(current.trim())

      // Build object from columns and values
      const obj = {}
      columns.forEach((col, idx) => {
        if (idx < values.length) {
          let value = values[idx]

          // Remove quotes if present
          if (value.startsWith('"') && value.endsWith('"')) {
            value = value.slice(1, -1)
          }

          // Type conversion based on column name or value
          if (col === 'confidence') {
            obj[col] = parseFloat(value)
          } else if (!isNaN(value) && value !== '' && !col.includes('text') && !col.includes('id') && !col.includes('reference')) {
            // Convert to number if it's numeric and not a text/id field
            obj[col] = parseFloat(value)
          } else {
            obj[col] = value
          }
        }
      })

      return obj
    },

    /**
     * Parses TOON key:value format (simple or multi-line)
     *
     * Handles simple key-value TOON responses where each line contains a key:value pair.
     * Automatically converts values to appropriate types (booleans, numbers, strings)
     * and removes surrounding quotes when present.
     *
     * @private
     * @param {Array<string>} lines - Cleaned lines from the TOON response
     * @param {Object} options - Parsing options from parent call
     * @param {Function} options.validator - Optional validator function to validate final result
     * @returns {Object|null} Parsed object with key-value pairs and type-converted values, or null if validation fails
     *
     * @example
     * // Single-line format
     * this.parseToonKeyValueFormat(['confidence: 95'], {})
     * // Returns: {confidence: 95}
     *
     * @example
     * // Multi-line format with type conversion
     * this.parseToonKeyValueFormat([
     *   'message: Hello World',
     *   'ended: true',
     *   'currentOffer: 150.5'
     * ], {})
     * // Returns: {message: 'Hello World', ended: true, currentOffer: 150.5}
     *
     * @example
     * // With quoted values
     * this.parseToonKeyValueFormat(['address: "Main St, City"', 'confidence: 90'], {})
     * // Returns: {address: 'Main St, City', confidence: 90}
     */
    parseToonKeyValueFormat(lines, options) {
      const result = {}

      for (const line of lines) {
        const colonIndex = line.indexOf(':')
        if (colonIndex === -1) continue

        const key = line.substring(0, colonIndex).trim()
        let value = line.substring(colonIndex + 1).trim()

        // Remove quotes if present
        if ((value.startsWith('"') && value.endsWith('"')) ||
            (value.startsWith("'") && value.endsWith("'"))) {
          value = value.slice(1, -1)
        }

        // Type conversion based on value content
        if (value === 'true' || value === 'false') {
          result[key] = value === 'true'
        } else if (!isNaN(value) && value !== '') {
          result[key] = parseFloat(value)
        } else {
          result[key] = value
        }
      }

      console.log('Parsed AI TOON (key-value):', result)

      // Apply custom validator if provided
      if (options.validator && !options.validator(result)) {
        console.error('TOON validation failed:', result)
        return null
      }

      return result
    }
  }
}
